import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Typography, Tag, Button, Space, Divider, Avatar, message, Popconfirm } from 'antd';
import { 
  CalendarOutlined, 
  EyeOutlined, 
  MessageOutlined, 
  HeartOutlined,
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  UserOutlined
} from '@ant-design/icons';
import './index.css';

const { Title, Paragraph } = Typography;

const PostDetail = ({ posts, onUpdatePost, onDeletePost, user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLiked, setIsLiked] = useState(false);

  useEffect(() => {
    if (id && posts) {
      const postId = parseInt(id);
      const foundPost = posts.find(p => p.id === postId);
      
      if (foundPost) {
        setPost(foundPost);
        // 检查用户是否已经点赞过
        const likedPosts = JSON.parse(localStorage.getItem('liked_posts') || '[]');
        setIsLiked(likedPosts.includes(postId));
        
        // 更新阅读量
        if (onUpdatePost) {
          // const updatedPost = {
          //   ...foundPost,
          //   views: (foundPost.views || 0) + 1
          // };
          // onUpdatePost(postId.toString(), updatedPost);
        }
      } else {
        message.error('文章不存在！');
        navigate('/');
      }
      setLoading(false);
    }
  }, [id, posts, navigate, onUpdatePost]);

  const handleLike = () => {
    if (!user) {
      message.warning('请先登录后再点赞');
      navigate('/auth');
      return;
    }
    
    if (post && onUpdatePost) {
      const likedPosts = JSON.parse(localStorage.getItem('liked_posts') || '[]');
      let newLikes;
      let updatedLikedPosts;
      
      if (isLiked) {
        // 取消点赞
        newLikes = Math.max((post.likes || 0) - 1, 0);
        updatedLikedPosts = likedPosts.filter(pId => pId !== post.id);
        message.success('取消点赞');
      } else {
        // 点赞
        newLikes = (post.likes || 0) + 1;
        updatedLikedPosts = [...likedPosts, post.id];
        message.success('点赞成功！');
      }
      
      setIsLiked(!isLiked);
      localStorage.setItem('liked_posts', JSON.stringify(updatedLikedPosts));
      
      const updatedPost = {
        ...post,
        likes: newLikes
      };
      onUpdatePost(id, updatedPost);
      setPost(updatedPost);
    }
  };

  const handleEdit = () => {
    if (id && user) {
      const isAuthor = user.username === post.author || user.role === 'admin';
      if (isAuthor) {
        navigate(`/write/${id}`);
      } else {
        message.error('只有作者可以编辑文章');
      }
    }
  };

  const handleDelete = () => {
    if (onDeletePost && id && user) {
      const isAuthor = user.username === post.author || user.role === 'admin';
      if (isAuthor) {
        onDeletePost(id);
        message.success('文章删除成功！');
        navigate('/');
      } else {
        message.error('只有作者可以删除文章');
      }
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="post-detail-page">
        <div className="loading">加载中...</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="post-detail-page">
        <div className="not-found">
          <h2>文章不存在</h2>
          <Button type="primary" onClick={() => navigate('/')}>返回首页</Button>
        </div>
      </div>
    );
  }

  // 检查是否为作者
  const isAuthor = user && (user.username === post.author || user.role === 'admin');

  return (
    <div className="post-detail-page">
      <div className="post-detail-container">
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={handleBack}
          className="back-btn"
          type="text"
        >
          返回
        </Button>

        <Card className="post-detail-card">
          {/* 文章头部 */}
          <div className="decorative-corner corner-tl-decor"></div>
  <div className="decorative-corner corner-tr-decor"></div>
  <div className="decorative-corner corner-bl-decor"></div>
  <div className="decorative-corner corner-br-decor"></div> 
  
          <div className="post-header">
            <Title level={1} className="post-title">
              {post.title || '无标题'}
            </Title>
            
            <div className="post-meta">
              <Space size="large" wrap>
                <div className="meta-item">
                  <Avatar 
                    size="small" 
                    icon={<UserOutlined />}
                    src={`https://randomuser.me/api/portraits/${post.author === '小红' ? 'women' : 'men'}/${(post.id % 10) + 1}.jpg`}
                  />
                  <span className="author-name">{post.author || '匿名'}</span>
                </div>
                
                <div className="meta-item">
                  <CalendarOutlined />
                  <span>{post.date || '未知日期'}</span>
                </div>
                
                {/* <div className="meta-item">
                  <EyeOutlined />
                  <span>{post.views || 0} 阅读</span>
                </div> */}
                
                <div className="meta-item">
                  <MessageOutlined />
                  <span>{post.comments || 0} 评论</span>
                </div>
                
                <div className="meta-item">
                  <HeartOutlined />
                  <span>{post.likes || 0} 点赞</span>
                </div>
              </Space>
            </div>

            {post.image && (
              <div className="post-cover">
                <img src={post.image} alt={post.title} />
              </div>
            )}
          </div>

          <Divider />

          {/* 文章内容 */}
          <div className="post-content">
            {/* 使用 post.excerpt 而不是 post.summary */}
            {post.excerpt && (
              <div className="post-excerpt">
                <Paragraph className="post-summary">
                  {post.excerpt}
                </Paragraph>
              </div>
            )}
            
            <div className="post-body">
              {post.content || '文章内容为空'}
            </div>
          </div>

          {/* 文章标签 */}
          {post.tags && post.tags.length > 0 && (
            <>
              <Divider />
              <div className="post-tags">
                <Space size={[0, 8]} wrap>
                  {post.tags.map((tag, index) => (
                    <Tag key={index} color="blue">
                      {tag}
                    </Tag>
                  ))}
                </Space>
              </div>
            </>
          )}

          <Divider />

          {/* 文章操作 */}
          <div className="post-actions">
            <Space>
              <Button 
                type={isLiked ? "primary" : "default"}
                danger={isLiked}
                icon={<HeartOutlined />}
                onClick={handleLike}
                className="like-btn"
              >
                {isLiked ? '已点赞' : '点赞'} ({post.likes || 0})
              </Button>

              {isAuthor && (
                <>
                  <Button 
                    icon={<EditOutlined />}
                    onClick={handleEdit}
                  >
                    编辑
                  </Button>
                  
                  <Popconfirm
                    title="确定要删除这篇文章吗？"
                    description="删除后无法恢复"
                    onConfirm={handleDelete}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Button 
                      danger
                      icon={<DeleteOutlined />}
                    >
                      删除
                    </Button>
                  </Popconfirm>
                </>
              )}
            </Space>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PostDetail;